import {
    Box,
    Button,
    Field,
    Icon,
    Image,
    Input,
    InputGroup,
    Menu,
    Popover,
    Portal,
    useBreakpointValue,
} from "@chakra-ui/react";
import { CaretDownIcon, WarningIcon } from "@phosphor-icons/react/dist/ssr";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";

import { OPERATIONSARRAY, useLeadingMaps } from "@/hooks/useBuscadorHook";
import { useGoogleMapsLoaded } from "@/hooks/useGoogleMapsLoaded";
import { buildBuscarUrl } from "@/sections/buscar/buildBuscarUrl";
import { resolveFirstPrediction } from "@/sections/buscar/resolveFirstPrediction";

// The homepage's "all property types selected" default. Kept as a const
// so buildBuscarUrl can recognize this set as the empty/no-filter case
// and skip the `tipo` URL param. Buscar's filter convention is "empty
// array = all selected" — if we serialized the explicit 17 here, /Buscar
// would land with the pill in active-selected state instead of the
// cleaner default.
const ALL_PROPERTY_TYPES = [
    "HOUSE",
    "DUPLEX_HOUSE",
    "COTTAGE",
    "RANCH",
    "TH",
    "APARTMENT",
    "LOFT",
    "STUDIO",
    "PENTHOUSE",
    "TERRAIN",
    "OFFICE",
    "CONSULTING_ROOM",
    "SHOP",
    "SHOP_SHOPPING_CENTER",
    "EVENT_HALL",
    "WAREHOUSE",
    "INDUSTRIAL_BUILDING",
];

const BuscadorDesktop = () => {
    const router = useRouter();
    const refLocationSearch = useRef(null);
    const autocomplete = useRef(null);
    const isSubmitting = useRef(false);

    const [selectedPlace, setSelectedPlace] = useState(null);

    const [searchFilters, setSearchFilters] = useState({
        propertyType: ALL_PROPERTY_TYPES,
        operationLabel: "Comprar",
        operationValue: ["SALE"],
    });

    const [_query] = useLeadingMaps(autocomplete, refLocationSearch);
    const mapsLoaded = useGoogleMapsLoaded();

    const selectOperationType = useCallback((label, value) => {
        setSearchFilters((prev) => ({
            ...prev,
            operationLabel: label,
            operationValue: Array.isArray(value) ? value : [value],
        }));
    }, []);

    const submitHandler = useCallback(
        async (directPlace = null) => {
            if (isSubmitting.current) return;
            isSubmitting.current = true;

            try {
                const keyword = refLocationSearch.current?.value?.trim() || "";

                // Verificar código Wiggot
                const wiggotPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{7}$/;
                if (wiggotPattern.test(keyword)) {
                    await router.push(`/Propiedad/wig-${keyword}`);
                    return;
                }

                // Build a Spanish-keyed /Buscar URL (P6.9). Replaces the
                // legacy `queryGenerator` call, which produces English
                // keys that the new /Buscar SSR parser silently drops.
                // queryGenerator is still used by the regional landing
                // pages (DW.buscadores.1), so it stays in the codebase —
                // we just don't go through it for the homepage → /Buscar
                // route anymore.
                const placeToUse = directPlace || selectedPlace;
                // typeValue=null when the user hasn't narrowed from the
                // default — keeps the URL clean (Buscar's "all selected"
                // convention is an empty array, not the explicit 17).
                const typeValueForUrl =
                    searchFilters.propertyType.length === ALL_PROPERTY_TYPES.length
                        ? null
                        : searchFilters.propertyType;
                const finalQuery = buildBuscarUrl({
                    place: placeToUse,
                    typeValue: typeValueForUrl,
                    operationValue: searchFilters.operationValue,
                    fallbackKeyword: keyword,
                });

                await router.push(`/Buscar?${finalQuery}`);
            } catch (error) {
                console.error("Error en submitHandler:", error);
            } finally {
                isSubmitting.current = false;
            }
        },
        [router, searchFilters.propertyType, searchFilters.operationValue, selectedPlace]
    );

    useEffect(() => {
        // Maps loads after mount now; re-attach when it (and the Autocomplete
        // instance useLeadingMaps creates on the same flip) become available.
        if (!mapsLoaded || !autocomplete.current) return;

        const handlePlaceChanged = async () => {
            const place = autocomplete.current.getPlace();
            const input = document.getElementById("lugar");
            if (place?.geometry) {
                setSelectedPlace(place);
                submitHandler(place);
                return;
            }
            // No direct pick — user typed text and hit Enter (or the
            // autocomplete reported no match). Try to resolve the first
            // prediction via Places before falling back to raw text, so
            // homepage Enter behaves the same as /Buscar's
            // Enter-resolves-first-prediction.
            const trimmed = input?.value?.trim();
            if (!trimmed) return;
            const resolved = await resolveFirstPrediction(trimmed);
            if (resolved) {
                setSelectedPlace(resolved);
                submitHandler(resolved);
                return;
            }
            // Soft fallback: no predictions available → submit raw text
            // as `q`. /Buscar lands with the text in its search bar; the
            // user can refine from there.
            const typeValueForUrl =
                searchFilters.propertyType.length === ALL_PROPERTY_TYPES.length
                    ? null
                    : searchFilters.propertyType;
            const finalQuery = buildBuscarUrl({
                place: null,
                typeValue: typeValueForUrl,
                operationValue: searchFilters.operationValue,
                fallbackKeyword: trimmed,
            });
            router.push(`/Buscar?${finalQuery}`);
        };

        const listener = autocomplete.current.addListener("place_changed", handlePlaceChanged);
        return () => window.google.maps.event.removeListener(listener);
    }, [mapsLoaded, submitHandler, selectedPlace]);

    const isGreaterThanMd = useBreakpointValue({ base: false, md: true });
    const isGreaterThanLg = useBreakpointValue({ base: false, lg: true });
    const [open, setOpen] = useState(false);

    return (
        <Box
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            borderRadius={{ base: "lg", md: "full" }}
            width="100%"
            height={{ base: "100px", md: "65px" }}
            alignSelf="start"
            bgColor="white"
            boxShadow="lg"
            overflow="hidden"
        >
            <Box width={{ base: "100%", md: "70%" }} h="65px">
                <Field.Root
                    ml={{ base: "0", md: "8" }}
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    display="contents"
                >
                    <Popover.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
                        <InputGroup
                            height="100%"
                            startElement={
                                <Box
                                    pointerEvents="none"
                                    height="100%"
                                    pl={{ base: "3", md: "4" }}
                                    pr="2"
                                    display="flex"
                                    alignItems="center"
                                >
                                    <Image
                                        src="https://media.grupoguia.mx/assets/home-page/Homepage-v4/icons/home-icon.svg"
                                        w={5}
                                        objectFit="contain"
                                        alt="Buscar"
                                    />
                                </Box>
                            }
                        >
                            <Input
                                ref={refLocationSearch}
                                fontSize={{ base: "2xs", md: "xs", lg: "sm", xl: "md" }}
                                alignContent="center"
                                pl={{ base: "4", md: "14" }}
                                pr={{ base: "4", md: "4" }}
                                id="lugar"
                                type="text"
                                variant="unstyled"
                                placeholder={
                                    isGreaterThanLg
                                        ? "Ingresa una ubicación (ej. Providencia, Guadalajara)"
                                        : "Ingresa una ubicación"
                                }
                                color="black"
                                fontWeight={500}
                                height="100%"
                                textAlign={{ base: "center", md: "left" }}
                                _focus={{ boxShadow: "none" }}
                            />
                        </InputGroup>
                        <Popover.Positioner>
                            <Popover.Content>
                                <Popover.Arrow>
                                    <Popover.ArrowTip />
                                </Popover.Arrow>
                                <Popover.CloseTrigger />
                                <Icon boxSize={3}>
                                    <WarningIcon weight="fill" />
                                </Icon>
                                <Popover.Body>Completa este campo</Popover.Body>
                            </Popover.Content>
                        </Popover.Positioner>
                    </Popover.Root>
                </Field.Root>
            </Box>

            {!isGreaterThanMd && (
                <Box
                    display="flex"
                    width="100%"
                    height="50px"
                    px="4"
                    alignItems="center"
                    justifyContent="space-between"
                    bgColor="white"
                    borderTop="1px solid #f0f0f0"
                >
                    <Menu.Root>
                        <Menu.Trigger
                            asChild
                            bgColor="#FFFFFF"
                            color="#000"
                            size="lg"
                            w="fit-content"
                            height="50px"
                            p="0"
                            fontSize={{ base: "xs", md: "sm" }}
                            type="button"
                            fontWeight="600"
                            textAlign="left"
                            _hover={{
                                background: "#F0F0F0",
                            }}
                            _open={{
                                background: "#F0F0F0",
                            }}
                        >
                            <Button>
                                <Box as="span" flex="1" textAlign="left">
                                    {searchFilters.operationLabel}
                                </Box>
                                <Icon boxSize={2}>
                                    <CaretDownIcon weight="bold" />
                                </Icon>
                            </Button>
                        </Menu.Trigger>
                        <Portal>
                            <Menu.Positioner>
                                <Menu.Content shadow="lg" minW="200px" py={2}>
                                    {OPERATIONSARRAY.map(({ value, label }) => (
                                        <Menu.Item
                                            key={value}
                                            value={value}
                                            onClick={() => selectOperationType(label, value)}
                                            fontSize="xs"
                                            _hover={{
                                                background: "#F0F0F0",
                                                cursor: "pointer",
                                            }}
                                            p={2}
                                        >
                                            {label}
                                        </Menu.Item>
                                    ))}
                                </Menu.Content>
                            </Menu.Positioner>
                        </Portal>
                    </Menu.Root>
                    <Button
                        bgColor="#002B49"
                        minW={7}
                        h={7}
                        aria-label="Buscar propiedades"
                        borderRadius="full"
                        p="0"
                        border="none"
                        fontSize="sm"
                        onClick={() => submitHandler()}
                        type="button"
                        color="white"
                        _hover={{ bgColor: "brand.600" }}
                        _active={{ bgColor: "brand.600" }}
                        fontWeight="500"
                        flex="none"
                    >
                        <Image
                            src="https://media.grupoguia.mx/assets/home-page/Homepage-v4/icons/lupa.svg"
                            w={3}
                            objectFit="contain"
                            alt="Buscar"
                            filter="brightness(0) invert(1)"
                        />
                    </Button>
                </Box>
            )}

            {isGreaterThanMd && (
                <>
                    <Box width="20%" height="65px" borderRadius="full" borderRightRadius={0}>
                        <Menu.Root>
                            <Menu.Trigger
                                asChild
                                bgColor="#FFFFFF"
                                color="#000"
                                size="lg"
                                width="100%"
                                height="100%"
                                borderRadius="full"
                                borderRightRadius={0}
                                p="0"
                                mx="auto"
                                border="0"
                                fontSize="lg"
                                type="button"
                                fontWeight="600"
                                _hover={{
                                    background: "#F0F0F0",
                                }}
                                _open={{
                                    background: "#F0F0F0",
                                }}
                            >
                                <Button>
                                    {searchFilters.operationLabel}{" "}
                                    <Icon boxSize={3}>
                                        <CaretDownIcon weight="bold" />
                                    </Icon>
                                </Button>
                            </Menu.Trigger>
                            <Portal>
                                <Menu.Positioner>
                                    <Menu.Content minW="200px" py={2}>
                                        {OPERATIONSARRAY.map(({ value, label }) => (
                                            <Menu.Item
                                                key={value}
                                                value={value}
                                                onClick={() => selectOperationType(label, value)}
                                                _hover={{
                                                    background: "#F0F0F0",
                                                    cursor: "pointer",
                                                }}
                                                p={2}
                                            >
                                                {label}
                                            </Menu.Item>
                                        ))}
                                    </Menu.Content>
                                </Menu.Positioner>
                            </Portal>
                        </Menu.Root>
                    </Box>
                    <Box width="10%" borderRadius="full" alignContent="center">
                        <Button
                            bgColor="#002B49"
                            size="lg"
                            w={{ md: "45px", lg: "50px" }}
                            h={{ md: "45px", lg: "50px" }}
                            aria-label="Buscar propiedades"
                            borderRadius="full"
                            p="0"
                            mx="auto"
                            border="none"
                            fontSize="sm"
                            onClick={() => submitHandler()}
                            type="button"
                            color="white"
                            _hover={{ bgColor: "brand.600" }}
                            _active={{ bgColor: "brand.600" }}
                            fontWeight="500"
                        >
                            <Image
                                src="https://media.grupoguia.mx/assets/home-page/Homepage-v4/icons/lupa.svg"
                                w={5}
                                objectFit="contain"
                                alt="Buscar"
                                filter="brightness(0) invert(1)"
                            />
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default BuscadorDesktop;
